import { useEffect, useState } from 'react';
import KbDocumentToolbar from './KbDocumentToolbar';
import KbDocumentList from './KbDocumentList';
import KbDocumentSummary from './KbDocumentSummary';
import KbDocumentLifecycle from './KbDocumentLifecycle';
import KbDocumentUpload from './KbDocumentUpload';
import KbDocumentEditModal from './KbDocumentEditModal';
import KbDocumentTextEditor from './KbDocumentTextEditor';
import {
  getKbStatus,
  reindexAllKbDocuments,
  reindexKbDocument,
  publishKbDocument,
  deleteKbDocument,
} from '../api/backend';

function KbDocuments() {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    document_type: '',
    published: '',
    search: '',
  });
  const [status, setStatus] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showVersionUpload, setShowVersionUpload] = useState(false);
  const [textEditor, setTextEditor] = useState(null);

  async function refreshStatus() {
    try {
      const data = await getKbStatus();
      setStatus(data);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRefresh() {
    setActionLoading('refresh');
    setError(null);
    try {
      await refreshStatus();
      setRefreshTick((t) => t + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReindexAll() {
    setActionLoading('reindex-all');
    setError(null);
    try {
      await reindexAllKbDocuments();
      setRefreshTick((t) => t + 1);
      await refreshStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReindexSelected() {
    if (!selectedDocument) return;
    setActionLoading('reindex');
    setError(null);
    try {
      await reindexKbDocument(selectedDocument.id);
      setRefreshTick((t) => t + 1);
      await refreshStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  }

  // Publish toggling and archiving are the KB lifecycle operations that lived
  // in the Admin API (src/api/v1/admin/kb.py) without UI exposure (Roadmap).
  async function handlePublish() {
    if (!selectedDocument) return;
    setActionLoading('publish');
    setError(null);
    try {
      const updated = await publishKbDocument(selectedDocument.id, !selectedDocument.is_published);
      if (updated) setSelectedDocument(updated);
      setRefreshTick((t) => t + 1);
      await refreshStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete() {
    if (!selectedDocument) return;
    const label = selectedDocument.title || `ID ${selectedDocument.id}`;
    if (!window.confirm(`Поместить документ «${label}» в архив? Документ снимается с публикации; удалённая версия не восстанавливается из этой кнопки.`)) {
      return;
    }
    setActionLoading('delete');
    setError(null);
    try {
      await deleteKbDocument(selectedDocument.id);
      setSelectedDocument(null);
      setRefreshTick((t) => t + 1);
      await refreshStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  }

  const handleUpload = () => {
    setShowUpload(true);
  };

  const handleUploadDone = () => {
    setShowUpload(false);
    setRefreshTick((t) => t + 1);
    refreshStatus();
  };

  const handleEditDone = () => {
    setShowEdit(false);
    setRefreshTick((t) => t + 1);
    refreshStatus();
  };

  const handleVersionUploadDone = () => {
    setShowVersionUpload(false);
    setRefreshTick((t) => t + 1);
    refreshStatus();
  };

  const handleOpenTextEditor = ({ stage }) => {
    if (!selectedDocument) return;
    setTextEditor({
      documentId: selectedDocument.id,
      versionId: selectedDocument.active_version_id || selectedDocument.id,
      stage,
    });
  };

  const handleTextEditorDone = () => {
    setTextEditor(null);
    setRefreshTick((t) => t + 1);
    refreshStatus();
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  if (showUpload) {
    return (
      <div className="ai-config-page">
        <KbDocumentUpload mode="document" onDone={handleUploadDone} />
      </div>
    );
  }

  return (
    <div className="ai-config-page">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-semibold text-ai-text">База знаний. Документы</h2>
        <button
          onClick={handleRefresh}
          disabled={actionLoading === 'refresh'}
          className="ai-btn-outline rounded-ai px-3 py-1.5 text-sm"
          type="button"
        >
          {actionLoading === 'refresh' ? '…' : 'Обновить'}
        </button>
      </div>

      <KbDocumentToolbar
        status={status}
        selectedDocument={selectedDocument}
        onUpload={handleUpload}
        onUploadVersion={() => setShowVersionUpload(true)}
        onEdit={() => setShowEdit(true)}
        onReindexSelected={handleReindexSelected}
        onReindexAll={handleReindexAll}
        onPublish={handlePublish}
        onDelete={handleDelete}
        actionLoading={actionLoading}
      />

      {error && (
        <div className="ai-error mb-3 text-sm">{error}</div>
      )}

      {/* Same macro-panel widths as the operational consoles: document list
          420px, expanded lifecycle panel 320px, summary takes the rest. */}
      <div className="flex flex-col lg:flex-row gap-3 min-h-0 flex-1">
        <div className="min-h-0 w-full flex-shrink-0 lg:w-[420px]">
          <KbDocumentList
            filters={filters}
            onFiltersChange={setFilters}
            selectedDocument={selectedDocument}
            onSelectDocument={setSelectedDocument}
            refreshTick={refreshTick}
          />
        </div>

        <div className="min-h-0 flex-1">
          <KbDocumentSummary
            documentId={selectedDocument?.id}
            refreshTick={refreshTick}
            onAction={() => {
              setRefreshTick((t) => t + 1);
              refreshStatus();
            }}
            onOpenTextEditor={handleOpenTextEditor}
          />
        </div>

        <div className="min-h-0 w-full flex-shrink-0 lg:w-[320px]">
          <KbDocumentLifecycle documentId={selectedDocument?.id} refreshTick={refreshTick} />
        </div>
      </div>

      {showEdit && selectedDocument && (
        <KbDocumentEditModal
          document={selectedDocument}
          onDone={handleEditDone}
          onCancel={() => setShowEdit(false)}
        />
      )}

      {showVersionUpload && selectedDocument && (
        <KbDocumentUpload
          mode="version"
          documentId={selectedDocument.id}
          onDone={handleVersionUploadDone}
        />
      )}

      {textEditor && (
        <KbDocumentTextEditor
          documentId={textEditor.documentId}
          versionId={textEditor.versionId}
          stage={textEditor.stage}
          onDone={handleTextEditorDone}
          onCancel={() => setTextEditor(null)}
        />
      )}
    </div>
  );
}

export default KbDocuments;
